import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePurchases, useCreatePurchase, useUpdatePurchase, useDeletePurchase } from '@/hooks/usePSI';
import { usePurchaseOrders, useUpdatePOStatus, useDeletePurchaseOrder } from '@/hooks/usePurchaseOrder';
import { IPurchase, IPurchaseCreateRequest } from '@/types/psi';
import { POStatus, PO_STATUS_LABELS, PO_STATUS_COLORS, POResponse } from '@/types/purchaseorder';
import { formatCurrency } from '@/services/poService';
import SearchBar from '@/components/SearchBar';
import Pagination from '@/components/Pagination';
import LoadingSpinner from '@/components/LoadingSpinner';
import PurchaseOrderDialog from './PurchaseOrderDialog';
import InboundShipmentDialog from './InboundShipmentDialog';
import { MobileCard } from '@/components/ui';
import { toast } from 'react-hot-toast';
import AgenticFlowDialog from '../purchaseflow/AgenticFlowDialog';

type TabType = 'sheet' | 'orders';

const PurchaseList: React.FC = () => {
  const { t } = useTranslation('psi');
  const navigate = useNavigate();
  const { subtab } = useParams<{ subtab?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // ?flow=3_price_confirm opens AgenticFlowDialog with that fixture
  // ?flow=new opens a fresh flow
  const flowParam = searchParams.get('flow');
  const [showFlowDialog, setShowFlowDialog] = useState(!!flowParam);

  const handleCloseFlowDialog = () => {
    setShowFlowDialog(false);
    // Remove ?flow from URL without navigation
    searchParams.delete('flow');
    setSearchParams(searchParams, { replace: true });
  };

  // Open dialog when ?flow param appears
  useEffect(() => {
    if (flowParam) setShowFlowDialog(true);
  }, [flowParam]);

  // Determine active tab from URL or default to 'orders'
  const getTabFromUrl = (urlSubtab?: string): TabType => {
    if (urlSubtab === 'records') return 'sheet';
    return 'orders'; // Default to orders for 'orders' subtab or no subtab
  };

  const [activeTab, setActiveTab] = useState<TabType>(() => getTabFromUrl(subtab));

  // Sync tab state with URL changes
  useEffect(() => {
    setActiveTab(getTabFromUrl(subtab));
  }, [subtab]);

  // Handle tab change - update URL
  const handleTabChange = (tab: TabType) => {
    const subtabPath = tab === 'orders' ? 'orders' : 'records';
    navigate(`/app/psi/purchases/${subtabPath}`);
  };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showPODialog, setShowPODialog] = useState(false);
  const [showInboundDialog, setShowInboundDialog] = useState(false);
  const [editingPO, setEditingPO] = useState<POResponse | null>(null);
  const [newPurchase, setNewPurchase] = useState<Partial<IPurchaseCreateRequest>>({
    date: new Date().toISOString().split('T')[0],
    status: 'pending',
  });

  // Sheet-based purchases (existing)
  const { data, isLoading, refetch } = usePurchases({ page, pageSize, q: searchQuery });
  const createMutation = useCreatePurchase();
  const updateMutation = useUpdatePurchase();
  const deleteMutation = useDeletePurchase();

  // Firestore-based POs (new)
  const { data: poData, isLoading: poLoading, refetch: poRefetch } = usePurchaseOrders();
  const updatePOStatusMutation = useUpdatePOStatus();
  const deletePOMutation = useDeletePurchaseOrder();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleAddNew = async () => {
    if (!newPurchase.supplier || !newPurchase.item) {
      alert(t('validation.supplierAndItemRequired'));
      return;
    }

    try {
      await createMutation.mutateAsync(newPurchase as IPurchaseCreateRequest);
      setIsAddingNew(false);
      setNewPurchase({ date: new Date().toISOString().split('T')[0], status: 'pending' });
    } catch (error) {
      logger.error('Failed to create purchase:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm(t('purchase.confirmDelete'))) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        logger.error('Failed to delete purchase:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // PO handlers
  const handleDeletePO = async (id: string) => {
    if (confirm('確定要刪除這張採購單嗎？')) {
      try {
        await deletePOMutation.mutateAsync(id);
        toast.success('採購單已刪除');
      } catch (error) {
        toast.error(`刪除失敗: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleUpdatePOStatus = async (id: string, status: POStatus) => {
    try {
      await updatePOStatusMutation.mutateAsync({ id, status });
      toast.success(`狀態已更新為 ${PO_STATUS_LABELS[status]}`);
    } catch (error) {
      toast.error(`更新失敗: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const currentLoading = activeTab === 'orders' ? poLoading : isLoading;
  if (currentLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => handleTabChange('orders')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            採購單
          </button>
          <button
            onClick={() => handleTabChange('sheet')}
            className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
              activeTab === 'sheet'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            進貨紀錄
          </button>
        </nav>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 sm:justify-between sm:items-center">
        {activeTab === 'sheet' ? (
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t('purchase.searchPlaceholder')}
          />
        ) : (
          <div className="hidden sm:block" />
        )}
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => activeTab === 'orders' ? poRefetch() : refetch()}
            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[44px] active:scale-[0.98] transition-all"
          >
            {t('common.refresh')}
          </button>
          {activeTab === 'orders' ? (
            <button
              onClick={() => setShowPODialog(true)}
              className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 min-h-[44px] active:scale-[0.98] transition-all"
            >
              <span className="hidden xs:inline">新增</span>採購單
            </button>
          ) : (
            <button
              onClick={() => setShowInboundDialog(true)}
              className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 min-h-[44px] active:scale-[0.98] transition-all"
            >
              <span className="hidden xs:inline">新增</span>進貨單
            </button>
          )}
        </div>
      </div>

      {/* PO Dialog - Create */}
      {showPODialog && (
        <PurchaseOrderDialog
          onClose={() => setShowPODialog(false)}
          onSuccess={() => poRefetch()}
        />
      )}

      {/* PO Dialog - Edit */}
      {editingPO && (
        <PurchaseOrderDialog
          editingPO={editingPO}
          onClose={() => setEditingPO(null)}
          onSuccess={() => poRefetch()}
        />
      )}

      {/* Inbound Shipment Dialog */}
      {showInboundDialog && (
        <InboundShipmentDialog
          onClose={() => setShowInboundDialog(false)}
          onSuccess={() => refetch()}
        />
      )}

      {/* Purchase Orders - Mobile Card View */}
      {activeTab === 'orders' && (
        <>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {poData?.purchase_orders?.map((po) => (
              <MobileCard
                key={po.po_id}
                title={po.po_number}
                subtitle={po.vendor?.name || po.vendor_code}
                status={
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${PO_STATUS_COLORS[po.status]}`}>
                    {PO_STATUS_LABELS[po.status]}
                  </span>
                }
                fields={[
                  { label: '採購日期', value: new Date(po.purchase_date).toLocaleDateString() },
                  { label: '交貨日期', value: new Date(po.expected_delivery).toLocaleDateString() },
                  { label: '總金額', value: formatCurrency(po.grand_total, po.currency) },
                ]}
                actions={[
                  { label: '編輯', onClick: () => setEditingPO(po), variant: 'secondary' },
                  ...(po.sheet_url ? [{
                    label: '表單',
                    onClick: () => window.open(po.sheet_url, '_blank'),
                    variant: 'primary' as const,
                  }] : []),
                  { label: '刪除', onClick: () => handleDeletePO(po.po_id), variant: 'danger' },
                ]}
              />
            ))}
            {(!poData?.purchase_orders || poData.purchase_orders.length === 0) && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                尚無採購單
              </div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">採購單號</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">廠商</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">採購日期</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">交貨日期</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">總金額</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {poData?.purchase_orders?.map((po) => (
                  <tr key={po.po_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                      {po.sheet_url ? (
                        <a
                          href={po.sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {po.po_number}
                        </a>
                      ) : (
                        po.po_number
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {po.vendor?.name || po.vendor_code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(po.purchase_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(po.expected_delivery).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(po.grand_total, po.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={po.status}
                        onChange={(e) => handleUpdatePOStatus(po.po_id, e.target.value as POStatus)}
                        className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${PO_STATUS_COLORS[po.status]}`}
                      >
                        <option value="pending">待審核</option>
                        <option value="approved">已核准</option>
                        <option value="ordered">已下單</option>
                        <option value="received">已收貨</option>
                        <option value="closed">已結案</option>
                        <option value="cancelled">已取消</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingPO(po)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="編輯"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {po.sheet_url && (
                          <a
                            href={po.sheet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="開啟表單"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                        <button
                          onClick={() => handleDeletePO(po.po_id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="刪除"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!poData?.purchase_orders || poData.purchase_orders.length === 0) && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      尚無採購單
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Sheet-based Purchases */}
      {activeTab === 'sheet' && (
        <>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {data?.purchases?.map((purchase: IPurchase) => (
              <MobileCard
                key={purchase.purchase_id}
                title={purchase.item}
                subtitle={`#${purchase.purchase_id} - ${purchase.supplier}`}
                status={
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(purchase.status)}`}>
                    {purchase.status}
                  </span>
                }
                fields={[
                  { label: t('purchase.columns.date'), value: purchase.date },
                  { label: t('purchase.columns.quantity'), value: purchase.quantity },
                  { label: t('purchase.columns.unitPrice'), value: `$${purchase.unit_price.toFixed(2)}` },
                  { label: t('purchase.columns.total'), value: `$${purchase.total.toFixed(2)}` },
                ]}
                actions={[
                  { label: t('common.delete'), onClick: () => handleDelete(purchase.purchase_id), variant: 'danger' },
                ]}
              />
            ))}
            {(!data?.purchases || data.purchases.length === 0) && !isAddingNew && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {t('purchase.noData')}
              </div>
            )}
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.id')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.supplier')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.item')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.quantity')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.unitPrice')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.total')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('purchase.columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {isAddingNew && (
                  <tr className="bg-green-50 dark:bg-green-900/20">
                    <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">{t('common.auto')}</td>
                    <td className="px-4 py-2">
                      <input
                        type="date"
                        value={newPurchase.date || ''}
                        onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        placeholder={t('purchase.columns.supplier')}
                        value={newPurchase.supplier || ''}
                        onChange={(e) => setNewPurchase({ ...newPurchase, supplier: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        placeholder={t('purchase.columns.item')}
                        value={newPurchase.item || ''}
                        onChange={(e) => setNewPurchase({ ...newPurchase, item: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        placeholder={t('common.qty')}
                        value={newPurchase.quantity || ''}
                        onChange={(e) => setNewPurchase({ ...newPurchase, quantity: parseInt(e.target.value) || 0 })}
                        className="w-20 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        step="0.01"
                        placeholder={t('common.price')}
                        value={newPurchase.unit_price || ''}
                        onChange={(e) => setNewPurchase({ ...newPurchase, unit_price: parseFloat(e.target.value) || 0 })}
                        className="w-24 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-400 dark:text-gray-500">
                      ${((newPurchase.quantity || 0) * (newPurchase.unit_price || 0)).toFixed(2)}
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={newPurchase.status || 'pending'}
                        onChange={(e) => setNewPurchase({ ...newPurchase, status: e.target.value })}
                        className="px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600"
                      >
                        <option value="pending">{t('status.pending')}</option>
                        <option value="received">{t('status.received')}</option>
                        <option value="cancelled">{t('status.cancelled')}</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={handleAddNew}
                        disabled={createMutation.isLoading}
                        className="text-green-600 hover:text-green-800 mr-2"
                      >
                        {t('common.save')}
                      </button>
                      <button
                        onClick={() => setIsAddingNew(false)}
                        className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                      >
                        {t('common.cancel')}
                      </button>
                    </td>
                  </tr>
                )}
                {data?.purchases?.map((purchase: IPurchase) => (
                  <tr key={purchase.purchase_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{purchase.purchase_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{purchase.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{purchase.supplier}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{purchase.item}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{purchase.quantity}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${purchase.unit_price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">${purchase.total.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(purchase.status)}`}>
                        {purchase.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(purchase.purchase_id)}
                        disabled={deleteMutation.isLoading}
                        className="text-red-600 hover:text-red-800"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
                {(!data?.purchases || data.purchases.length === 0) && !isAddingNew && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      {t('purchase.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination (sheet tab only) */}
      {activeTab === 'sheet' && data && data.total_pages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={data.total_pages}
          totalItems={data.total_count || 0}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}

      {/* Summary (sheet tab only) */}
      {activeTab === 'sheet' && (
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
          {t('purchase.showingPurchases', { count: data?.purchases?.length || 0, total: data?.total_count || 0 })}
        </div>
      )}

      {/* Summary (orders tab) */}
      {activeTab === 'orders' && (
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
          共 {poData?.purchase_orders?.length || 0} 張採購單
        </div>
      )}

      {/* Agentic PO Flow Dialog — opened via ?flow=new | ?flow=<fixture_key> | ?flow=<flowId> */}
      {showFlowDialog && flowParam && (
        <AgenticFlowDialog
          isOpen={true}
          onClose={handleCloseFlowDialog}
          initialFixture={flowParam !== 'new' && !flowParam.startsWith('mock-flow-') ? flowParam : undefined}
          flowId={flowParam.startsWith('mock-flow-') ? flowParam : undefined}
        />
      )}
    </div>
  );
};

export default PurchaseList;
